import React, { useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import MedicationForm from 'components/individual/medications/MedicationForm';
import { useAuth } from 'hooks/useAuth';

const DISMISSED_KEY = 'dischargeMedicationPrompt.dismissed';

// Per-device convenience only: a dismissed drug comes back on another phone, which
// is harmless. Storage can be blocked, so every access is guarded.
const readDismissed = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'));
  } catch {
    return new Set();
  }
};
const writeDismissed = (set) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
  } catch {
    // Blocked storage: the dismissal lasts until the page reloads.
  }
};

const normalize = (value) => String(value || '').trim().toLowerCase();

// RxNorm names run long ("Amoxicillin 500 MG Oral Capsule"), so either name
// containing the other counts as already on the list.
const isOnList = (name, medications) => {
  const n = normalize(name);
  return medications.some((m) => {
    const existing = normalize(m.name);
    return existing && (existing.includes(n) || n.includes(existing));
  });
};

/**
 * Offers to add drugs named in a visit's discharge instructions to My Medications.
 *
 * "Mentioned", never "prescribed": an instruction carries only a medicationName,
 * and "stop taking ibuprofen" names a drug just as "start amoxicillin" does. So the
 * patient decides, and the form opens with only the name filled in (essentialOnly,
 * so a dose they do not know is not required, rather than invented).
 *
 * Shows nothing while medications are loading or failed to load: an empty list
 * would make every drug look missing.
 */
const DischargeMedicationPrompt = ({ deliveryId, instructions }) => {
  const { medications, medicationsLoading, medicationsLoadError, addMedication } = useAuth();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [adding, setAdding] = useState(null);

  if (medicationsLoading || medicationsLoadError) return null;

  const key = (name) => `${deliveryId}:${normalize(name)}`;
  const names = [
    ...new Set(
      (instructions || [])
        .filter((i) => i.category === 'medication_specific' && i.medicationName)
        .map((i) => i.medicationName.trim()),
    ),
  ].filter((name) => !isOnList(name, medications || []) && !dismissed.has(key(name)));

  if (!names.length) return null;

  const dismiss = (name) => {
    const next = new Set(dismissed);
    next.add(key(name));
    writeDismissed(next);
    setDismissed(next);
  };

  return (
    <>
      <Stack spacing={1} sx={{ mb: 2 }}>
        {names.map((name) => (
          <Alert
            key={name}
            severity="info"
            action={
              <Stack direction="row" spacing={0.5}>
                <Button color="inherit" size="small" onClick={() => dismiss(name)}>
                  Not now
                </Button>
                <Button color="inherit" size="small" onClick={() => setAdding({ name })}>
                  Add
                </Button>
              </Stack>
            }
          >
            <Typography variant="body2">
              Your provider mentioned <strong>{name}</strong>. Add it to your medications?
            </Typography>
          </Alert>
        ))}
      </Stack>

      <MedicationForm
        open={Boolean(adding)}
        onClose={() => setAdding(null)}
        onSave={addMedication}
        // Held in state, not built inline: MedicationForm resets whenever this
        // object changes, so a fresh one per render would wipe what the patient types.
        medication={adding}
        essentialOnly
      />
    </>
  );
};

export default DischargeMedicationPrompt;
